import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL as API } from '../config';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, RefreshCw, LogOut, CheckCircle2 } from 'lucide-react';
import { MyEnAbLogo } from '../components/MyEnAbLogo';
import { Toaster, toast } from 'sonner';
import SEO from '../components/SEO';

export default function VerificationPending() {
    const { user, logout } = useAuth();
    const [email, setEmail] = useState(user?.email || '');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [countdown, setCountdown] = useState(0);

    const handleResend = async () => {
        if (!email) {
            toast.error('Please enter your email address.');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${API}/auth/resend-verification`, { email });
            toast.success(response.data.message || 'Verification email sent!');
            setSent(true);
            // Start 60s countdown
            setCountdown(60);
            const interval = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        setSent(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (error.response?.status === 429) {
                toast.error(detail || 'Please wait before requesting another email.');
            } else {
                toast.error(detail || 'Failed to resend verification email.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        window.location.href = '/login';
    };

    return (
        <div className="min-h-screen bg-[#121212] flex items-center justify-center px-4 py-8">
            <SEO title="Verify Your Email" description="Please verify your email address to access MyEnAb." />
            <Toaster position="top-center" richColors />

            <div className="w-full max-w-md text-center">
                <div className="flex justify-center mb-8">
                    <MyEnAbLogo className="w-48 h-12" />
                </div>

                <Card className="bg-[#18181B] border-[#27272A]">
                    <CardHeader>
                        <div className="flex justify-center mb-4">
                            <div className="w-20 h-20 bg-inclusion-gold/10 rounded-full flex items-center justify-center">
                                <Mail className="w-10 h-10 text-inclusion-gold" />
                            </div>
                        </div>
                        <CardTitle className="font-lexend text-2xl">Verify Your Email</CardTitle>
                        <CardDescription className="text-zinc-400">
                            Your account has been created, but you need to verify your email address before you can access the platform.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800">
                            <div className="flex items-start gap-3 text-left">
                                <CheckCircle2 className="w-5 h-5 text-zinc-500 mt-0.5 shrink-0" />
                                <div className="text-sm text-zinc-400">
                                    <p>We sent a verification link to your email. Click the link to activate your account.</p>
                                    <p className="mt-2 text-zinc-500">Check your <strong className="text-zinc-300">spam/junk folder</strong> if you don't see it in your inbox.</p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="space-y-2 text-left">
                                <Label htmlFor="resend-email" className="text-zinc-200 text-sm">
                                    Didn't receive the email? Enter your email to resend:
                                </Label>
                                <Input
                                    id="resend-email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input-dark"
                                    data-testid="resend-email-input"
                                />
                            </div>

                            <Button
                                onClick={handleResend}
                                disabled={loading || sent}
                                className="w-full btn-primary"
                                data-testid="resend-verification-btn"
                            >
                                {loading ? (
                                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                                ) : sent ? (
                                    `Resend in ${countdown}s`
                                ) : (
                                    <><RefreshCw className="w-4 h-4 mr-2" /> Resend Verification Email</>
                                )}
                            </Button>
                        </div>

                        <div className="pt-2 border-t border-zinc-800">
                            <Button
                                variant="ghost"
                                onClick={handleLogout}
                                className="w-full text-zinc-400 hover:text-white hover:bg-zinc-800"
                                data-testid="verification-logout-btn"
                            >
                                <LogOut className="w-4 h-4 mr-2" /> Sign out and use a different account
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Inclusion colors decoration */}
                <div className="flex justify-center gap-2 mt-8" aria-hidden="true">
                    <span className="w-3 h-3 rounded-full bg-[#E40303]"></span>
                    <span className="w-3 h-3 rounded-full bg-[#FF8C00]"></span>
                    <span className="w-3 h-3 rounded-full bg-[#FFD700]"></span>
                    <span className="w-3 h-3 rounded-full bg-[#008026]"></span>
                    <span className="w-3 h-3 rounded-full bg-[#24408E]"></span>
                    <span className="w-3 h-3 rounded-full bg-[#732982]"></span>
                </div>
            </div>
        </div>
    );
}
